// Copyright ©️ 2026 eVolve MEP, LLC

import type { GetPresignedUrlResponse, MarkupPage } from '@evolvemep/api-ts';

import { downloadBlobViaLinkElement } from 'components/Mantine/FileUploads/downloadFileViaLinkElement';

/** Used with useFileCache to download a file from S3, apply markup, and trigger a download of the marked-up file. */
export const downloadFileResponseWithMarkup = async (response: GetPresignedUrlResponse, markupPages: MarkupPage[]) => {
  // Lazy import of dependencies.
  const { PDFDocument } = await import('pdf-lib');
  const { jsPDF } = await import('jspdf');
  await import('svg2pdf.js');

  const preSignedUrl = response.preSignedUrl;
  if (!preSignedUrl) return;
  const fileBuffer = await fetch(preSignedUrl).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(fileBuffer);

  const pdfPages = pdfDoc.getPages();

  // Apply markup to each page of the PDF.
  for (const markupPage of markupPages) {
    const markupString = markupPage.markup;
    if (!markupString) continue;

    // Note: Markup pages are 1-indexed and PDF pages are 0-indexed.
    const pdfPageNumber = markupPage.page - 1;
    const pdfPage = pdfPages[pdfPageNumber];
    if (!pdfPage) continue;
    // Use the crop box for both sizing and positioning so they stay consistent.
    const cropBox = pdfPage.getCropBox();

    const widthInPt = cropBox.width;
    const heightInPt = cropBox.height;

    const parser = new DOMParser();
    const svgDocument = parser.parseFromString(markupString, 'image/svg+xml');
    const svgElement = svgDocument.documentElement;

    const docMetadataElement = svgElement.querySelector('metadata markup_document');
    const leftOffsetInInches = Number(docMetadataElement?.getAttribute('data-document-offset-left-in-inches') ?? '0');
    const topOffsetInInches = Number(docMetadataElement?.getAttribute('data-document-offset-top-in-inches') ?? '0');
    const docWidthInInches = Number(docMetadataElement?.getAttribute('data-document-width-in-inches') ?? '0');
    const docHeightInInches = Number(docMetadataElement?.getAttribute('data-document-height-in-inches') ?? '0');

    // Get the original dimensions and viewBox for the SVG canvas to re-calculate the new viewBox.
    const svgCanvasWidthInPx = Number(svgElement.getAttribute('width') ?? '0');
    const svgCanvasHeightInPx = Number(svgElement.getAttribute('height') ?? '0');
    const viewBoxDimensions = (svgElement.getAttribute('viewBox') ?? '').split(' ').map(Number);
    const vbMinXInInches = viewBoxDimensions[0] ?? 0;
    const vbMinYInInches = viewBoxDimensions[1] ?? 0;
    const vbWidthInInches = viewBoxDimensions[2] ?? 1;

    const pixelsPerInch = svgCanvasWidthInPx / vbWidthInInches;
    const svgCanvasHeightInInches = svgCanvasHeightInPx / pixelsPerInch;

    // Calculate the correct viewBox dimensions for positioning the markup on the page.
    const updatedVbMinX = vbMinXInInches + leftOffsetInInches;
    // The calculation for the minY of the new viewBox is a *bit* more complex because of the y-axis flip that Autodesk applies to the SVG.
    const updatedVbMinY =
      (vbMinYInInches + docHeightInInches + (svgCanvasHeightInInches - (topOffsetInInches + docHeightInInches))) * -1;

    svgElement.setAttribute('width', String(widthInPt));
    svgElement.setAttribute('height', String(heightInPt));
    svgElement.setAttribute('viewBox', `${updatedVbMinX} ${updatedVbMinY} ${docWidthInInches} ${docHeightInInches}`);
    svgElement.removeAttribute('style');

    // Autodesk applies a CSS transform of scale(1,-1) to the root svg element to flip the coordinate system to be y-up.
    // svg2pdf doesn't respect the top-level style attribute, so we need a wrapper for the children to flip the markups.
    const flipGroup = svgDocument.createElementNS('http://www.w3.org/2000/svg', 'g');
    flipGroup.setAttribute('transform', 'scale(1,-1)');
    [...svgElement.childNodes].forEach((childNode) => flipGroup.appendChild(childNode));
    svgElement.appendChild(flipGroup);

    // The text output from svg2pdf is inexplicably (yet consistently) off-center along the y-axis
    // by exactly 3.4x the original y value of the first tspan.
    // The cause isn't obvious, but the workaround is reliable.
    svgElement.querySelectorAll('text').forEach((textElement) => {
      const tSpanElements = textElement.querySelectorAll('tspan');
      if (tSpanElements.length === 0) return;
      const initialY = Number(tSpanElements[0]?.getAttribute('y') ?? 0);
      const offsetY = initialY - initialY / 3.4;
      tSpanElements.forEach((tSpanElement) => {
        const prevY = Number(tSpanElement.getAttribute('y') ?? 0);
        const updatedY = prevY - offsetY;
        tSpanElement.setAttribute('y', String(updatedY));
      });
    });

    // Create a PDF to represent the markup layer.
    const orientation = widthInPt > heightInPt ? 'landscape' : 'portrait';
    const unit = 'pt';
    const svgPdfDoc = new jsPDF(orientation, unit, [widthInPt, heightInPt]);
    await svgPdfDoc.svg(svgElement);
    const svgPdfBytes = svgPdfDoc.output('arraybuffer');

    // Load markup layer on top of existing PDF page:
    const [svgPdfPage] = await pdfDoc.embedPdf(svgPdfBytes, [0]);
    if (!svgPdfPage) continue;
    // Draw the markup at the page's crop box origin so it aligns with the visible content of the page, even
    // when it has a non-zero origin (e.g. a centered origin rather than the conventional bottom-left origin).
    pdfPage.drawPage(svgPdfPage, { x: cropBox.x, y: cropBox.y });
  }

  const bytes = await pdfDoc.save();
  const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
  downloadBlobViaLinkElement(response.fileName, blob);
};
